import React, { useState, useEffect, useCallback } from 'react';
import { getPubliekeLink, vernieuwPubliekeLink } from '../api/api';
import { fmtDatumNL, leesbareFout } from '../util/format';

/* De publieke pagina van een inrichting, voor de behandelaar.
 *
 * Dit is sinds 06-09-2026 een paneel en geen venster meer: het hangt onder
 * Correspondentie (zie CorrespondentieModal), samen met het Melding-document.
 * Het venster eromheen, de titel en het sluiten zitten daar.
 *
 * Twee dingen in een venster, met opzet niet als drie gelijke knoppen: de link
 * is dagelijks werk, verversen is onomkeerbaar voor wie de oude link al heeft.
 * Vandaar dat verversen onderaan staat, met een bevestiging.
 *
 * De mailtekst komt van de server (publieke_link.py), zodat het kenmerk en het
 * aantal putten in de mail niet kunnen afwijken van wat de melder op zijn
 * pagina ziet. Wat hier gebeurt is alleen tonen, laten aanpassen en kopieren.
 */

const LINKKLEUR = '#1351b4';
const URL_PATROON = /https?:\/\/\S+/;

/* Het lettertype dat mee op het klembord gaat. Aptos is sinds 2024 de
   standaard van Office; Calibri is de terugval voor wie hem niet heeft, en
   sans-serif voor de rest. Puntgrootte en geen pixels: Word rekent pixels om
   op een manier die je niet wil.

   Let op wat dit niet kan: staat iemands Outlook bij het plakken op "opmaak
   van doel samenvoegen" of "alleen tekst behouden", dan gooit hij dit weg en
   neemt hij zijn eigen standaard. Dat is dan hoogstwaarschijnlijk hetzelfde,
   maar afdwingen kunnen we het van hier niet. */
const MAIL_FONT = "font-family:Aptos,Calibri,'Segoe UI',sans-serif;font-size:11pt";
const HOOFD_FONT = "font-family:Aptos,Calibri,'Segoe UI',sans-serif;font-size:10pt";

/* De aanhef, in de vormen die in deze teksten voorkomen. Dient alleen om te
   herkennen dat een tekst er meteen mee begint -- zie briefhoofd(). */
const AANHEF_PATROON = /^(beste|geachte|hallo|hoi)\b/i;

/* Het briefhoofd: de eerste regel van de tekst, met daarin aan wie de brief
   gericht is. Vet en een punt kleiner, zoals een adressering bovenaan een
   brief.

   De regel is met opzet aan de plek gebonden en niet aan een opmaakcode: de
   behandelaar mag deze tekst aanpassen, en dan moet hij geen tekens kunnen
   slopen die hij niet kent. Haalt hij de regel weg, dan begint de tekst met de
   aanhef en is er gewoon geen briefhoofd meer. */
function briefhoofd(index, regels) {
  return index === 0 && regels.length === 1 && !AANHEF_PATROON.test(regels[0].trim());
}

/* Blokken zijn gescheiden door een lege regel. Staat er meer dan een regel in
   een blok, dan is de eerste regel de kop; een blok van een regel is gewoon een
   alinea. Zo kan er een losse alinea onder een kop staan zonder dat die zelf
   vet wordt, en hoeft de behandelaar geen opmaakcodes te leren. */
export function blokken(tekst) {
  return String(tekst || '')
    .split(/\n{2,}/)
    .map((b) => b.split('\n').filter((r, i, a) => r.trim() || i < a.length - 1))
    .filter((regels) => regels.join('').trim() !== '');
}

const esc = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* De kale link in de tekst wordt in de mail de naam van de inrichting. Het
   pijltje staat buiten de link: blauw, maar niet onderstreept. */
function regelHtml(regel, data) {
  const m = regel.match(URL_PATROON);
  if (!m) return esc(regel);
  return esc(regel.slice(0, m.index))
    + `<a href="${esc(m[0])}" style="color:${LINKKLEUR};text-decoration:underline">${esc(data.label)}</a>`
    + `<span style="color:${LINKKLEUR};text-decoration:none">&nbsp;↗</span>`
    + esc(regel.slice(m.index + m[0].length));
}

/* Dezelfde tekst, maar dan zoals Outlook hem hoort te krijgen. Bewust kaal:
   geen klassen, geen tabellen, alleen wat elke mailclient aankan.

   De witregel tussen de alinea's is een lege div en geen marge op een <p>.
   Outlook plakt met de Word-engine en geeft binnenkomende alinea's zijn eigen
   stijl met marge nul; de witregel valt dan weg. Een lege regel-div is wat
   Outlook zelf schrijft als je op enter drukt, en die overleeft het plakken. */
export function naarHtml(tekst, data) {
  return blokken(tekst).map((regels, i) => {
    if (briefhoofd(i, regels)) {
      return `<div style="${HOOFD_FONT}"><b>${esc(regels[0])}</b></div>`;
    }
    const inhoud = regels.map((r, j) => (
      j === 0 && regels.length > 1 ? `<b>${esc(r)}</b>` : regelHtml(r, data)
    )).join('<br>');
    return `<div style="${MAIL_FONT}">${inhoud}</div>`;
  }).join(`<div style="${MAIL_FONT}"><br></div>`);
}

/* Plakken met opmaak en zonder. Wie HTML aankan (Outlook, Gmail) krijgt de
   vette koppen en de link onder de naam van de inrichting; een kale client
   krijgt dezelfde tekst met de link uitgeschreven. Lukt het allebei niet, dan
   alsnog platte tekst -- liever iets op het klembord dan niets. */
export async function kopieerRijk(tekst, data) {
  const html = naarHtml(tekst, data);
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([tekst], { type: 'text/plain' }),
      })]);
      return true;
    }
  } catch (e) { /* valt hieronder terug */ }
  return kopieerPlat(tekst);
}

export async function kopieerPlat(tekst) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(tekst);
      return true;
    }
  } catch (e) { /* valt hieronder terug */ }
  try {
    // Zonder https bestaat navigator.clipboard niet; dit werkt daar wel.
    const el = document.createElement('textarea');
    el.value = tekst;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const gelukt = document.execCommand('copy');
    document.body.removeChild(el);
    return gelukt;
  } catch (e) {
    return false;
  }
}

/* Wie de link aanmaakte is alleen interessant als het een mens was. De trigger
   en de migratie zetten er een woord in dat in een zin niet werkt. */
function maker(data) {
  const d = (data.aangemaakt_door || '').trim();
  if (!d || d === 'automatisch' || d === 'register' || d.startsWith('backfill')) return null;
  return d;
}

export function Regel({ regel, data }) {
  const m = regel.match(URL_PATROON);
  if (!m) return <>{regel}</>;
  return (
    <>
      {regel.slice(0, m.index)}
      <a className="publink-maillink" href={m[0]} target="_blank" rel="noreferrer">{data.label}</a>
      <span className="publink-mailpijl">&nbsp;↗</span>
      {regel.slice(m.index + m[0].length)}
    </>
  );
}

/* Het voorbeeld in het venster. Gebruikt dezelfde blokindeling en dezelfde
   briefhoofdregel als naarHtml, zodat wat je ziet ook is wat je plakt. */
export function MailVoorbeeld({ tekst, data }) {
  return (
    <div className="publink-voorbeeld">
      {blokken(tekst).map((regels, i) => (
        briefhoofd(i, regels)
          ? <p key={i} className="publink-briefhoofd">{regels[0]}</p>
          : (
            <p key={i}>
              {regels.map((r, j) => (
                <React.Fragment key={j}>
                  {j > 0 && <br />}
                  {j === 0 && regels.length > 1
                    ? <strong>{r}</strong>
                    : <Regel regel={r} data={data} />}
                </React.Fragment>
              ))}
            </p>
          )
      ))}
    </div>
  );
}

export function PubliekeLinkPaneel({ onClose, inrichtingId }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [tekst, setTekst] = useState('');
  const [bewerken, setBewerken] = useState(false);
  const [gekopieerd, setGekopieerd] = useState(null);   // 'link' | 'mail' | 'mislukt'
  const [verversStap, setVerversStap] = useState(false);
  const [verversBezig, setVerversBezig] = useState(false);

  const laad = useCallback(async (levend) => {
    try {
      const res = await getPubliekeLink(inrichtingId);
      if (levend && !levend.current) return;
      setData(res.data);
      setTekst(res.data.mailtekst || '');
    } catch (e) {
      if (levend && !levend.current) return;
      setFout(leesbareFout(e));
    }
  }, [inrichtingId]);

  useEffect(() => {
    const levend = { current: true };
    setData(null); setFout(null); setTekst('');
    setBewerken(false); setGekopieerd(null); setVerversStap(false);
    laad(levend);
    return () => { levend.current = false; };
  }, [laad]);

  // De melding "Gekopieerd" hoort weer te verdwijnen, anders lijkt een tweede
  // klik niets te doen.
  useEffect(() => {
    if (!gekopieerd) return undefined;
    const t = setTimeout(() => setGekopieerd(null), 2500);
    return () => clearTimeout(t);
  }, [gekopieerd]);

  const kopieerLink = async () => {
    const ok = await kopieerPlat(data.url);
    setGekopieerd(ok ? 'link' : 'mislukt');
  };

  const kopieerMail = async () => {
    const ok = await kopieerRijk(tekst, data);
    setGekopieerd(ok ? 'mail' : 'mislukt');
  };

  const ververs = async () => {
    setVerversBezig(true);
    try {
      const res = await vernieuwPubliekeLink(inrichtingId);
      setData(res.data);
      setTekst(res.data.mailtekst || '');
      setVerversStap(false);
      setBewerken(false);
    } catch (e) {
      setFout(leesbareFout(e));
    } finally {
      setVerversBezig(false);
    }
  };

  return (
    <>
      {fout && <div className="alert alert-error" style={{ marginTop: 0 }}>{fout}</div>}

      {!data && !fout && (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Bezig met ophalen…</p>
      )}

      {data && (
        <>
          <div className="publink-blok">
            <div className="publink-kop">
              <h3 className="publink-titel">Link voor de melder</h3>
              <button className="btn btn-ghost btn-sm" onClick={kopieerLink}>
                {gekopieerd === 'link' ? 'Gekopieerd' : 'Kopiëren'}
              </button>
            </div>
            <input className="publink-url" readOnly value={data.url}
                   onFocus={(e) => e.target.select()} />
            <p className="publink-meta">
              Link van {fmtDatumNL(data.aangemaakt_op)}{maker(data) ? `, aangemaakt door ${maker(data)}` : ''}.
              Wie deze link heeft, kan de pagina bekijken. Er staan geen
              persoonsgegevens op, wel putlocaties en perceelnummers.
            </p>
          </div>

          <div className="publink-blok">
            <div className="publink-kop">
              <h3 className="publink-titel">Mailtekst</h3>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setBewerken((b) => !b)}>
                  {bewerken ? 'Voorbeeld' : 'Aanpassen'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={kopieerMail}>
                  {gekopieerd === 'mail' ? 'Gekopieerd' : 'Kopiëren'}
                </button>
              </div>
            </div>

            {bewerken ? (
              <>
                <textarea className="publink-tekstvak" value={tekst}
                          onChange={(e) => setTekst(e.target.value)} />
                <p className="publink-meta">
                  Een lege regel begint een nieuw blok. Heeft een blok meer dan
                  één regel, dan wordt de eerste regel de vette kop. De link
                  wordt in de mail “{data.label}”.
                </p>
              </>
            ) : (
              <>
                <MailVoorbeeld tekst={tekst} data={data} />
                <p className="publink-meta">
                  Zo komt de tekst in Outlook te staan. In een mailprogramma
                  zonder opmaak vervalt het vet en staat de link uitgeschreven.
                </p>
              </>
            )}
          </div>

          {gekopieerd === 'mislukt' && (
            <p className="publink-meta" style={{ color: '#b91c1c' }}>
              Kopiëren lukte niet. Selecteer de tekst en kopieer met Cmd+C.
            </p>
          )}

          {verversStap ? (
            <div className="publink-waarschuwing">
              <p style={{ margin: 0 }}>
                <strong>Weet je het zeker?</strong> De huidige link stopt meteen
                met werken. Wie hem al heeft, krijgt te zien dat de link niet
                meer geldig is. Doe dit als de link bij de verkeerde persoon
                terecht is gekomen of als de inrichting van houder wisselt.
              </p>
              <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setVerversStap(false)}>
                  Annuleren
                </button>
                <button className="btn btn-danger btn-sm" onClick={ververs} disabled={verversBezig}>
                  {verversBezig ? 'Bezig…' : 'Ja, nieuwe link'}
                </button>
              </div>
            </div>
          ) : (
            <div className="publink-voet">
              <button className="btn btn-ghost btn-sm" onClick={() => setVerversStap(true)}>
                Link verversen
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Sluiten</button>
            </div>
          )}
        </>
      )}
    </>
  );
}
